import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  MouseEvent,
} from 'react';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { RadialImpactChartArc } from 'apps/sigma2-ui/src/components/key-factors/radial-impact-chart/RadialImpactChartArc';
import useMeasure from 'react-use-measure';
import { KeyFactorsGradedImpactDataPoint } from 'apps/sigma2-ui/src/components/key-factors/types';
import { RadialImpactChartCalloutCard } from 'apps/sigma2-ui/src/components/key-factors/radial-impact-chart/RadialImpactChartCalloutCard';
import { getRingRadius } from 'apps/sigma2-ui/src/components/key-factors/radial-impact-chart/utils';
import { ringOutlineStyle } from 'apps/sigma2-ui/src/components/key-factors/radial-impact-chart/utils';
import { ringStyle } from 'apps/sigma2-ui/src/components/key-factors/radial-impact-chart/utils';
import { useIndexedMeasurements } from 'apps/sigma2-ui/src/components/key-factors/radial-impact-chart/utils';
import { LightTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import { IconInformationSvg } from 'apps/sigma2-ui/src/components/svg/IconInformation';
import { KeyFactorsRadialImpactChartSerializableProps } from 'libs/end-to-end/src/success-phenotype';

export interface RadialImpactChartProps
  extends KeyFactorsRadialImpactChartSerializableProps {
  handleSelectTrait: (controlVariableColumnHeaderValue: string) => void;
  setIsTraitHovered: (index: number, next: boolean) => void;
}

export const RadialImpactChart: React.FC<RadialImpactChartProps> = ({
  dataPoints,
  handleSelectTrait,
  isTraitHovered,
  setIsTraitHovered,
  dataQualityScoreLevel,
  numberOfPeople,
}) => {
  const language = useLanguage();
  const gradedImpactDataPoint: KeyFactorsGradedImpactDataPoint[] = useMemo(
    () =>
      dataPoints
        .sort((a, b) => b.impact - a.impact)
        .slice(0, 6)
        .map((d) => ({
          ...d,
          grade: d.impact >= 20 ? 'high' : d.impact >= 10 ? 'medium' : 'low',
        })),
    [dataPoints],
  );

  const [parentContainerRef, parentContainerMeasurements] = useMeasure({
    debounce: 100,
  });
  const [centerCircleRef, centerCircleMeasurements] = useMeasure();

  // this ID is necessary to force a re-render of the component when the parent container is resized.
  // this is a workaround to avoid issues when dashboards are resized.
  const [renderUniqueId, setRenderUniqueId] = useState(0);

  useEffect(() => {
    setRenderUniqueId((prevId) => prevId + 1);
  }, [parentContainerMeasurements]);

  const ringCount = useMemo(
    () => gradedImpactDataPoint.length,
    [gradedImpactDataPoint.length],
  );

  const [cardMeasurements, setCardMeasurement] =
    useIndexedMeasurements(ringCount);

  const ringCenterX = useMemo(
    () =>
      centerCircleMeasurements.left -
      parentContainerMeasurements.left +
      centerCircleMeasurements.width / 2,
    [
      centerCircleMeasurements.left,
      parentContainerMeasurements.left,
      centerCircleMeasurements.width,
    ],
  );

  const ringCenterY = useMemo(
    () => parentContainerMeasurements.height / 2,
    [parentContainerMeasurements.height],
  );

  const ringRadiusList = useMemo(
    () =>
      gradedImpactDataPoint.map((_, i) =>
        getRingRadius({
          ringIndex: i,
          totalRings: ringCount,
          parentHeight: parentContainerMeasurements.height,
        }),
      ),
    [gradedImpactDataPoint, ringCount, parentContainerMeasurements.height],
  );

  const onChartMouseMove = useCallback(
    (event: MouseEvent) => {
      const mouseX = event.clientX - parentContainerMeasurements.left;
      const mouseY = event.clientY - parentContainerMeasurements.top;

      // Distance from mouse to center
      const dx = mouseX - ringCenterX;
      const dy = mouseY - ringCenterY;
      const distance = Math.hypot(dx, dy);

      // Assume each ring is between radiusList[i-1] and radiusList[i]
      let hitIndex = -1;
      for (let i = ringCount - 1; i >= 0; i--) {
        const outerRadius = ringRadiusList[i];
        const innerRadius = ringRadiusList[i + 1] ?? 0;
        if (distance <= outerRadius && distance >= innerRadius) {
          hitIndex = i;
          break;
        }
      }

      for (let i = 0; i < ringCount; i++) {
        setIsTraitHovered(i, i === hitIndex);
      }
    },
    [
      parentContainerMeasurements.left,
      parentContainerMeasurements.top,
      ringCenterX,
      ringCenterY,
      ringCount,
      ringRadiusList,
      setIsTraitHovered,
    ],
  );

  const onChartMouseLeave = useCallback(
    () => isTraitHovered.forEach((h, i) => setIsTraitHovered(i, false)),
    [isTraitHovered, setIsTraitHovered],
  );

  return (
    <div
      key={renderUniqueId}
      ref={parentContainerRef}
      className="relative flex-1 overflow-hidden max-w-[900px] min-h-[625px] m-10"
    >
      <div className="flex flex-row items-stretch h-full">
        <Spacing
          direction="vertical"
          size="md"
          className="w-fit max-w-full whitespace-nowrap"
        >
          <div className="flex items-center gap-3 mb-8 my-4">
            <S2Typography
              variant={'body-7'}
              textColorVariant={'gray-500'}
              translatedContent={language.keyFactorsRadialChartPercentImpact()}
            />
            <div className="flex flex-1 items-center gap-[1px]">
              <div className="bg-[#E7E8E8] rounded-l-2xl py-[0.125rem] pl-3 pr-6">
                <S2Typography
                  variant={'small-label-bold'}
                  textColorVariant="gray-800"
                  translatedContent="0-9%"
                />
              </div>
              <div className="bg-[#EFE2FD] py-[0.125rem] px-3">
                <S2Typography
                  variant={'small-label-bold'}
                  textColorVariant="violet-800"
                  translatedContent="10-19%"
                />
              </div>
              <div className="bg-[#DFE6F9] rounded-r-2xl py-[0.125rem] pl-3 pr-6">
                <S2Typography
                  variant={'small-label-bold'}
                  textColorVariant="royal-800"
                  translatedContent="> 20%"
                />
              </div>
            </div>
          </div>
          <div className="flex flex-col flex-1 gap-4">
            {gradedImpactDataPoint.map((r, i) => (
              <RadialImpactChartCalloutCard
                key={i}
                index={i}
                onMeasure={({ index, measurement }) =>
                  setCardMeasurement(index, measurement)
                }
                ring={r}
                isTraitHovered={isTraitHovered}
                setIsTraitHovered={setIsTraitHovered}
                handleSelectTrait={handleSelectTrait}
              />
            ))}
          </div>
          <div className="flex items-center gap-3 pt-8 pb-6">
            <div className="flex items-center gap-2">
              <S2Typography
                as={'span'}
                variant={'body-6'}
                textColorVariant="gray-500"
                translatedContent={language.keyFactorsRadialChartNumberOfPeople(
                  {
                    numberOfPeople,
                  },
                )}
              />

              <LightTooltip
                title={
                  <S2Typography
                    as={'span'}
                    variant={'body-7'}
                    textColorVariant="gray-1000"
                    translatedContent={language.numberOfPeopleTooltip()}
                  />
                }
                arrow
              >
                <span className="text-gray-600">
                  <IconInformationSvg svg={{ className: 'relative w-4 h-4' }} />
                </span>
              </LightTooltip>
            </div>
            <div className="flex items-center gap-2">
              <S2Typography
                as={'span'}
                variant={'body-6'}
                textColorVariant="gray-500"
                translatedContent={language.keyFactorsRadialChartAccuracy({
                  accuracy: dataQualityScoreLevel,
                })}
              />
              <LightTooltip
                title={
                  <S2Typography
                    as={'span'}
                    variant={'body-7'}
                    textColorVariant="gray-1000"
                    translatedContent={language.accuracyTooltip()}
                  />
                }
                arrow
              >
                <span className="text-gray-600">
                  <IconInformationSvg svg={{ className: 'relative w-4 h-4' }} />
                </span>
              </LightTooltip>
            </div>
          </div>
        </Spacing>

        <div
          style={{
            position: 'relative',
            left: 0,
            top: 0,
            flex: 1,
            padding: 0,
            margin: 0,
            fontSize: 0,
            whiteSpace: 'nowrap',
            minHeight: '625px',
          }}
          onMouseMove={onChartMouseMove}
          onMouseLeave={onChartMouseLeave}
        >
          {/* left side */}
          <div
            style={{
              display: 'inline-block',
              height: '100%',
              aspectRatio: '1 / 2',
              overflow: 'hidden',
              borderRight: '1px solid #FFF',
              verticalAlign: 'top',
            }}
          >
            <div
              style={{
                position: 'relative',
                height: '100%',
                aspectRatio: '1 / 1',
              }}
            >
              {gradedImpactDataPoint.map((r, i) => (
                <div
                  key={i}
                  onMouseEnter={() => setIsTraitHovered(i, true)}
                  onMouseLeave={() => setIsTraitHovered(i, false)}
                  style={ringStyle({
                    ...r,
                    index: i,
                    parentHeight: parentContainerMeasurements.height,
                    totalRings: gradedImpactDataPoint.length,
                    isHovered: isTraitHovered[i],
                  })}
                />
              ))}
            </div>
          </div>
          {/* right side */}
          <div
            style={{
              display: 'inline-block',
              height: '100%',
              aspectRatio: '1 / 2',
              overflow: 'hidden',
              verticalAlign: 'top',
            }}
          >
            <div
              style={{
                position: 'relative',
                height: '100%',
                aspectRatio: '1 / 1',
                transform: 'translateX(-50%)',
              }}
            >
              {gradedImpactDataPoint.map((r, i) => (
                <div
                  key={i}
                  style={ringOutlineStyle({
                    index: i,
                    parentHeight: parentContainerMeasurements.height,
                    totalRings: gradedImpactDataPoint.length,
                  })}
                />
              ))}
            </div>
          </div>
          {/* center circle */}
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              aspectRatio: '1 / 1',
            }}
          >
            <div
              style={{
                position: 'absolute',
                left: '40%',
                top: '40%',
                height: '20%',
                aspectRatio: '1 / 1',
                borderRadius: '50%',
                background: '#FFF',
                border: '1px solid #F7F7F7',
                filter: 'drop-shadow(-8px 6px 20px rgba(83, 122, 238, 0.06))',
              }}
            >
              <div
                ref={centerCircleRef}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <S2Typography
                  variant={'small-label'}
                  textColorVariant={'gray-800'}
                  translatedContent={language.keyFactorsRadialChartCenterLabel()}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {cardMeasurements.length === gradedImpactDataPoint.length &&
        gradedImpactDataPoint.map((r, i) => {
          return (
            <div
              key={i}
              className="absolute left-0 top-0 z-10 pointer-events-none"
            >
              <RadialImpactChartArc
                grade={r.grade}
                isHovered={isTraitHovered[i]}
                ringIndex={i}
                ringRadius={ringRadiusList[i]}
                ringCount={ringCount}
                startX={cardMeasurements[i].width}
                startY={
                  cardMeasurements[i].top -
                  parentContainerMeasurements.top +
                  cardMeasurements[i].height / 2
                }
                ringCenterX={ringCenterX}
                ringCenterY={ringCenterY}
                outermostRingRadius={getRingRadius({
                  ringIndex: 0,
                  totalRings: ringCount,
                  parentHeight: parentContainerMeasurements.height,
                })}
                parentHeight={parentContainerMeasurements.height}
                parentWidth={parentContainerMeasurements.width}
              />
            </div>
          );
        })}
    </div>
  );
};
